// src/components/MyAppBar/MyAppBar.tsx
// Advanced glass App Bar with gradient presets (persisted), command palette,
// quick-create, improved notifications, live chat unread, and inline nav progress.

import {
  useState,
  useEffect,
  useRef,
  MouseEvent,
  useMemo,
  useCallback,
} from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Avatar,
  Typography,
  CircularProgress,
  ListItemText,
  ListItemIcon,
  useScrollTrigger,
  LinearProgress,
  DialogContentText,
  TextField,
  List,
  ListItemButton,
  Radio,
  Stack,
  Badge,
  Tooltip,
  Paper,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddIcon from "@mui/icons-material/Add";
import MarkUnreadChatAltOutlinedIcon from "@mui/icons-material/MarkUnreadChatAltOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import KeyboardCommandKeyIcon from "@mui/icons-material/KeyboardCommandKey";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import SearchIcon from "@mui/icons-material/Search";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import ApartmentIcon from "@mui/icons-material/Apartment";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

import { useTheme, alpha, type Theme, styled, keyframes } from "@mui/material/styles";
import { Link, useNavigate } from "react-router-dom";
import ApprovalsDialog from "../../modules/hr/pages/Approvals";
import axios from "axios";

import { logOutRequest } from "../../utils/logout";
import { baseUrl } from "../../constants";
import { getUserInfoFromToken } from "../../utils/Main";
import { useUnreadTotal } from "../chat/useUnreadTotal";
import ChatDialog from "../chat/ChatDialog";

// Enhanced styled components
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  position: "relative",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover::before": {
    opacity: 1,
  },
  "&:hover": {
    transform: "scale(1.08) translateY(-1px)",
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const BrandContainer = styled(Box)<{ component?: any; to?: string }>(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
    transition: "left 0.5s ease",
  },
  "&:hover::after": {
    left: "100%",
  },
}));

interface MyAppBarProps {
  isMobile: boolean;
  drawerWidth: number;
  handleDrawerToggle: () => void;
  sx?: object;
}

interface Notification {
  id: number;
  title: string;
  body: string;
  entityId: number;
  createdAt: string;
}

const BAR_HEIGHT = 72;
const STORAGE_KEY = "appbarGradientStyle";

type GradientKey =
  | "subtle"
  | "ocean"
  | "sunset"
  | "emerald"
  | "midnight"
  | "graphite";

type CommandItem = {
  label: string;
  icon: React.ReactNode;
  path?: string;
  action?: () => void;
  hint?: string;
};

const GRADIENT_LABELS: Record<GradientKey, string> = {
  subtle: "Subtle Frost (Default)",
  ocean: "Ocean Breeze",
  sunset: "Sunset Glow",
  emerald: "Emerald Mist",
  midnight: "Midnight Pulse",
  graphite: "Graphite Sheen",
};

const gradientOverlay = (theme: Theme, key: GradientKey): string => {
  const dark = theme.palette.mode === "dark";
  const hi = dark ? 0.16 : 0.15;
  const lo = dark ? 0.06 : 0.05;

  switch (key) {
    case "ocean":
      return `linear-gradient(135deg, ${alpha(
        theme.palette.info.main,
        hi
      )} 0%, ${alpha(theme.palette.primary.light, lo)} 60%, transparent 100%)`;
    case "sunset":
      return `linear-gradient(135deg, ${alpha(
        theme.palette.warning.main,
        hi
      )} 0%, ${alpha(theme.palette.error.light, lo)} 60%, transparent 100%)`;
    case "emerald":
      return `linear-gradient(135deg, ${alpha(
        theme.palette.success.main,
        hi
      )} 0%, ${alpha(theme.palette.primary.main, lo)} 60%, transparent 100%)`;
    case "midnight":
      return `linear-gradient(135deg, ${alpha(
        theme.palette.secondary.main,
        hi
      )} 0%, ${alpha(theme.palette.info.dark, lo)} 60%, transparent 100%)`;
    case "graphite":
      return `linear-gradient(135deg, ${alpha(
        theme.palette.grey[800],
        hi
      )} 0%, ${alpha(theme.palette.grey[500], lo)} 60%, transparent 100%)`;
    case "subtle":
    default:
      return `linear-gradient(135deg, ${alpha(
        theme.palette.primary.light,
        dark ? 0.12 : 0.1
      )} 0%, ${alpha(
        theme.palette.primary.main,
        dark ? 0.06 : 0.05
      )} 55%, transparent 100%)`;
  }
};

// Read token from either storage
const getAuthToken = () =>
  localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");

const MyAppBar = ({
  isMobile,
  drawerWidth,
  handleDrawerToggle,
  sx,
}: MyAppBarProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const trigger = useScrollTrigger({ threshold: 8 });

  // Identity
  const user = getUserInfoFromToken();
  const workspaceName = user?.workspaceName ?? "Unknown Workspace";
  const profileImage = user?.profile?.imageUrl || null;
  const usernameInitial =
    (!profileImage && (user?.username?.[0] || "U").toUpperCase()) || null;
  const isAdmin = (user?.role ?? "user").toLowerCase() === "admin";

  // Menus & dialogs
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const [addAnchorEl, setAddAnchorEl] = useState<null | HTMLElement>(null);
  const [styleAnchorEl, setStyleAnchorEl] = useState<null | HTMLElement>(null);
  const [openLogout, setOpenLogout] = useState(false);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [approvalsDialogOpen, setApprovalsDialogOpen] = useState(false);

  // Notifications
  const [unseenNotifications, setUnseenNotifications] = useState<Notification[]>(
    []
  );
  const [notifLoading, setNotifLoading] = useState<boolean>(false);
  const initialUnseenIds = useRef<Set<number>>(new Set());
  const shownNotificationIds = useRef<Set<number>>(new Set());

  // Chat unread
  const { unreadTotal } = useUnreadTotal();

  // Inline route progress
  const [navigating, setNavigating] = useState(false);
  const withProgressNav = useCallback(
    (to: string) => {
      setNavigating(true);
      navigate(to);
      setTimeout(() => setNavigating(false), 500);
    },
    [navigate]
  );

  // Gradient selection (persisted)
  const [gradKey, setGradKey] = useState<GradientKey>(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as GradientKey | null;
    return saved && GRADIENT_LABELS[saved] ? saved : "subtle";
  });
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, gradKey);
  }, [gradKey]);

  const appBarBg = useMemo(() => {
    const base =
      theme.palette.mode === "dark"
        ? theme.palette.background.paper
        : "#ffffff";
    const glass = alpha(base, theme.palette.mode === "dark" ? 0.82 : 0.78);
    return `${gradientOverlay(theme, gradKey)}, ${glass}`;
  }, [theme, gradKey]);

  // Fetch notifications
  const fetchUnseenNotifications = async () => {
    setNotifLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        setUnseenNotifications([]);
        return;
      }
      const response = await axios.get(`${baseUrl}/notifications/unseen`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const unseen = response.data || [];
      unseen.forEach((n: Notification) => {
        const isNew = !initialUnseenIds.current.has(n.id);
        if (isNew) initialUnseenIds.current.add(n.id);
      });

      unseen.forEach((n: Notification) => {
        if (!shownNotificationIds.current.has(n.id)) {
          shownNotificationIds.current.add(n.id);
        }
      });

      setUnseenNotifications(unseen);
    } catch (err) {
      console.error("Error fetching unseen notifications", err);
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      if (!active) return;
      await fetchUnseenNotifications();
    })();

    const interval = setInterval(fetchUnseenNotifications, 10000);
    const handleExternalRefresh = () => fetchUnseenNotifications();

    window.addEventListener(
      "refresh-unseen-notifications",
      handleExternalRefresh
    );
    window.addEventListener("auth-token-changed", handleExternalRefresh);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener(
        "refresh-unseen-notifications",
        handleExternalRefresh
      );
      window.removeEventListener("auth-token-changed", handleExternalRefresh);
    };
  }, []);

  // Notification helpers
  const handleNotifClick = (event: MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
    fetchUnseenNotifications();
  };
  const handleNotifClose = () => setNotifAnchorEl(null);

  const handleSeenNotification = async (id: number) => {
    try {
      const token = getAuthToken();
      if (!token) return;
      await axios.patch(
        `${baseUrl}/notifications/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Error updating notification status", error);
    } finally {
      setUnseenNotifications((prev) => prev.filter((n) => n.id !== id));
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!unseenNotifications.length) return;
    const token = getAuthToken();
    if (!token) {
      setUnseenNotifications([]);
      return;
    }
    try {
      await Promise.all(
        unseenNotifications.map((n) =>
          axios.patch(
            `${baseUrl}/notifications/${n.id}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      setUnseenNotifications([]);
    } catch (err) {
      console.error("Mark all as read failed", err);
    }
  };

  const handleNotificationItemClick = (taskId: number) => {
    setNavigating(true);
    navigate(`/task/${taskId}`);
    setTimeout(() => setNavigating(false), 400);
    handleNotifClose();
  };

  // Add menu (Create)
  const handleAddClose = () => setAddAnchorEl(null);
  const goToNewTask = () => {
    handleAddClose();
    withProgressNav("/new-task/0?type=" + encodeURIComponent("Task"));
  };
  const goToReviewRequests = () => withProgressNav("/review-requests");

  // Profile menu
  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);

  // Logout
  const handleLogoutClick = () => {
    setAnchorEl(null);
    setOpenLogout(true);
  };
  const handleLogoutConfirm = async () => {
    try {
      await logOutRequest();
    } finally {
      setOpenLogout(false);
      navigate("/", { replace: true });
    }
  };
  const handleLogoutCancel = () => setOpenLogout(false);

  // Time ago utility
  const timeAgo = (iso: string) => {
    const diffMs = Date.now() - new Date(iso).getTime();
    const minutes = Math.round(diffMs / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  };

  // Command Palette (Ctrl/Cmd+K)
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cmdQuery, setCmdQuery] = useState("");
  const [cmdIndex, setCmdIndex] = useState(0);

  const commands: CommandItem[] = useMemo(() => {
    const base: CommandItem[] = [
      {
        label: "New Task",
        icon: <TaskAltIcon fontSize="small" />,
        path: "/new-task/0?type=Task",
        hint: "A",
      },
      {
        label: "Messenger",
        icon: <MarkUnreadChatAltOutlinedIcon fontSize="small" />,
        action: () => setChatDialogOpen(true),
      },
      {
        label: "Notifications",
        icon: <NotificationsIcon fontSize="small" />,
        path: "/notifications",
      },
      {
        label: "Review Requests",
        icon: <AssignmentIcon fontSize="small" />,
        path: "/review-requests",
      },
      {
        label: "Home",
        icon: <HomeOutlinedIcon fontSize="small" />,
        path: "/home",
      },
      {
        label: "Dashboard",
        icon: <DashboardOutlinedIcon fontSize="small" />,
        path: "/dashboard",
      },
      {
        label: "Projects",
        icon: <PeopleOutlineIcon fontSize="small" />,
        path: "/projects",
      },
      {
        label: "Departments",
        icon: <CorporateFareIcon fontSize="small" />,
        path: "/departments",
      },
      {
        label: "Workspace",
        icon: <ApartmentIcon fontSize="small" />,
        path: "/workspace",
      },
      {
        label: "Notebook",
        icon: <MenuBookOutlinedIcon fontSize="small" />,
        path: "/notebook",
      },
      {
        label: "Settings",
        icon: <SettingsIcon fontSize="small" />,
        path: "/settings",
      },
    ];
    if (isAdmin)
      base.splice(6, 0, {
        label: "Users",
        icon: <PeopleOutlineIcon fontSize="small" />,
        path: "/users",
      });
    return base;
  }, [isAdmin]);

  const filtered = useMemo(() => {
    if (!cmdQuery.trim()) return commands;
    const q = cmdQuery.toLowerCase();
    return commands.filter((c) => c.label.toLowerCase().includes(q));
  }, [cmdQuery, commands]);

  useEffect(() => {
    const onK = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const keyK = e.key.toLowerCase() === "k";
      if ((isMac && e.metaKey && keyK) || (!isMac && e.ctrlKey && keyK)) {
        e.preventDefault();
        setCmdOpen(true);
        setCmdQuery("");
        setCmdIndex(0);
      }
    };
    window.addEventListener("keydown", onK);
    return () => window.removeEventListener("keydown", onK);
  }, []);

  useEffect(() => {
    if (!cmdOpen) return;
    const onKeys = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setCmdOpen(false);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setCmdIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setCmdIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const sel = filtered[cmdIndex];
        if (!sel) return;
        setCmdOpen(false);
        if (sel.action) {
          sel.action();
        } else if (sel.path) {
          withProgressNav(sel.path);
        }
      }
    };
    window.addEventListener("keydown", onKeys);
    return () => window.removeEventListener("keydown", onKeys);
  }, [cmdOpen, filtered, cmdIndex, withProgressNav]);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        height: BAR_HEIGHT,
        width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
        ml: isMobile ? 0 : `${drawerWidth}px`,
        color: theme.palette.text.primary,
        background: appBarBg,
        borderBottom: `1px solid ${alpha(
          theme.palette.divider,
          trigger ? 0.95 : 0.7
        )}`,
        backdropFilter: "saturate(180%) blur(20px)",
        boxShadow: trigger 
          ? `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}, 0 0 0 1px ${alpha(theme.palette.divider, 0.1)}`
          : `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}, 0 0 0 1px ${alpha(theme.palette.divider, 0.05)}`,
        zIndex: (th) => th.zIndex.drawer + 1,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
          pointerEvents: "none",
        },
        ...sx,
      }}
    >
      {navigating && (
        <LinearProgress
          sx={{ position: "absolute", left: 0, right: 0, top: 0, height: 2 }}
        />
      )}

      <Toolbar
        sx={{
          height: BAR_HEIGHT,
          minHeight: BAR_HEIGHT,
          px: { xs: 1, sm: 2 },
          gap: 1,
        }}
      >
        {/* Left: menu (mobile) + brand */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flex: 1,
            minWidth: 0,
          }}
        >
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 0.25 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Enhanced NOVERA ERP Brand */}
          <BrandContainer
            component={Link as any}
            to="/home"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 1.5,
              borderRadius: 3,
              position: "relative",
              minWidth: "180px",
              textDecoration: "none",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                transform: "translateY(-2px)",
                borderColor: alpha(theme.palette.primary.main, 0.3),
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
              },
            }}
          >
            {/* Enhanced vertical accent line with gradient */}
            <Box
              sx={{
                width: "4px",
                height: "36px",
                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: "2px",
                flexShrink: 0,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
              }}
            />

            {/* Enhanced brand text */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.25,
              }}
            >
              {/* Enhanced NOVERA title with gradient */}
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                  fontWeight: 900,
                  letterSpacing: "-0.02em",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                  lineHeight: 1,
                  margin: 0,
                  transition: "all 0.3s ease",
                }}
              >
                NOVERA
              </Typography>

              {/* Enhanced ERP subtitle */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.15em",
                  fontFamily: '"Inter", "Roboto", sans-serif',
                  margin: 0,
                  opacity: 0.8,
                }}
              >
                ERP SYSTEM
              </Typography>
            </Box>
          </BrandContainer>

          {/* Enhanced Command Palette */}
          <Tooltip title="Command Palette (Ctrl/Cmd + K)">
            <Button
              variant="outlined"
              onClick={() => setCmdOpen(true)}
              startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
              endIcon={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.text.primary, 0.08),
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                  }}
                >
                  <KeyboardCommandKeyIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                  <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                    K
                  </Typography>
                </Box>
              }
              sx={{
                ml: 1,
                display: { xs: "none", md: "inline-flex" },
                color: "text.primary",
                textTransform: "none",
                borderRadius: 2.5,
                px: 2,
                py: 0.75,
                borderColor: alpha(theme.palette.divider, 0.5),
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: "blur(10px)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": { 
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  transform: "translateY(-1px)",
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                },
              }}
            >
              Search or jump…
            </Button>
          </Tooltip>
        </Box>

        {/* Enhanced Right Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1 },
            "& > *": {
              position: "relative",
            },
          }}
        >
          {/* Enhanced Style Palette */}
          <Tooltip title="App Bar Style">
            <StyledIconButton
              color="inherit"
              onClick={(e) => setStyleAnchorEl(e.currentTarget)}
              size="large"
              sx={{
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <ColorLensOutlinedIcon sx={{ fontSize: 22 }} />
            </StyledIconButton>
          </Tooltip>
          <Menu
            anchorEl={styleAnchorEl}
            open={Boolean(styleAnchorEl)}
            onClose={() => setStyleAnchorEl(null)}
            slotProps={{
              paper: { sx: { width: 320, borderRadius: 2, p: 0.5 } },
            }}
          >
            <Typography
              variant="overline"
              sx={{ px: 2, pt: 1, pb: 0.5, display: "block", opacity: 0.7 }}
            >
              App Bar Style
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            {(Object.keys(GRADIENT_LABELS) as GradientKey[]).map((key) => (
              <MenuItem
                key={key}
                onClick={() => {
                  setGradKey(key);
                  setStyleAnchorEl(null);
                }}
                selected={gradKey === key}
                sx={{ gap: 1 }}
              >
                <Radio checked={gradKey === key} size="small" />
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 24,
                      borderRadius: 1,
                      border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                      background: gradientOverlay(theme, key),
                      boxShadow: theme.shadows[1],
                    }}
                  />
                  <Typography variant="body2" noWrap>
                    {GRADIENT_LABELS[key]}
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  setGradKey("subtle");
                  setStyleAnchorEl(null);
                }}
              >
                Reset
              </Button>
            </Box>
          </Menu>

          {/* Enhanced Messenger */}
          <Tooltip title="Messenger">
            <StyledIconButton
              color="inherit"
              onClick={() => setChatDialogOpen(true)}
              aria-label="Open messenger"
              size="large"
              sx={{
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
              }}
            >
              <Badge
                color="error"
                max={99}
                badgeContent={unreadTotal}
                invisible={unreadTotal === 0}
                overlap="circular"
                sx={{ 
                  mr: 0.25,
                  "& .MuiBadge-badge": {
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.4)}`,
                    animation: unreadTotal > 0 ? `${pulse} 2s ease-in-out infinite` : "none",
                  },
                }}
              >
                <MarkUnreadChatAltOutlinedIcon sx={{ fontSize: 23 }} />
              </Badge>
            </StyledIconButton>
          </Tooltip>

          {/* Enhanced Approvals */}
          <Tooltip title="Approvals">
            <StyledIconButton
              color="inherit"
              onClick={() => setApprovalsDialogOpen(true)}
              aria-label="Open approvals"
              size="large"
              sx={{
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
              }}
            >
              <FactCheckOutlinedIcon sx={{ fontSize: 23 }} />
            </StyledIconButton>
          </Tooltip>

          {/* Enhanced Quick Add */}
          <Tooltip title="Quick Add">
            <StyledIconButton
              color="inherit"
              onClick={(e) => setAddAnchorEl(e.currentTarget)}
              size="large"
              sx={{
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.success.main, 0.05),
                border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                "&:hover": {
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                },
              }}
            >
              <AddIcon sx={{ fontSize: 24 }} />
            </StyledIconButton>
          </Tooltip>
          <Menu
            anchorEl={addAnchorEl}
            open={Boolean(addAnchorEl)}
            onClose={() => setAddAnchorEl(null)}
            slotProps={{
              paper: { sx: { width: 320, borderRadius: 2, p: 0.5 } },
            }}
          >
            <Typography
              variant="overline"
              sx={{ px: 2, pt: 1, pb: 0.5, display: "block", opacity: 0.7 }}
            >
              Create
            </Typography>

            <MenuItem onClick={goToNewTask}>
              <ListItemIcon>
                <TaskAltIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Task" secondary="Create a general task" />
              <ChevronRightIcon sx={{ opacity: 0.5 }} />
            </MenuItem>

            <MenuItem onClick={() => withProgressNav("/projects")}>
              <ListItemIcon>
                <ArticleOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Project" secondary="Create or manage projects" />
              <ChevronRightIcon sx={{ opacity: 0.5 }} />
            </MenuItem>
          </Menu>

          {/* Enhanced Review Requests */}
          <Tooltip title="Review Requests">
            <StyledIconButton 
              color="inherit" 
              onClick={goToReviewRequests} 
              size="large"
              sx={{
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.secondary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
              }}
            >
              <AssignmentIcon sx={{ fontSize: 23 }} />
            </StyledIconButton>
          </Tooltip>

          {/* Enhanced Notifications */}
          <Tooltip title="Notifications">
            <StyledIconButton 
              color="inherit" 
              onClick={handleNotifClick} 
              size="large"
              sx={{
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.error.main, 0.05),
                border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
              }}
            >
              <Badge
                badgeContent={unseenNotifications.length}
                color="error"
                overlap="circular"
                invisible={unseenNotifications.length === 0}
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.4)}`,
                    animation: unseenNotifications.length > 0 ? `${pulse} 2s ease-in-out infinite` : "none",
                  },
                }}
              >
                <NotificationsIcon sx={{ fontSize: 23 }} />
              </Badge>
            </StyledIconButton>
          </Tooltip>

          <Menu
            anchorEl={notifAnchorEl}
            open={Boolean(notifAnchorEl)}
            onClose={handleNotifClose}
            slotProps={{
              paper: { 
                sx: { 
                  width: 420, 
                  borderRadius: 3, 
                  p: 0.5,
                  background: theme.palette.mode === "dark"
                    ? alpha(theme.palette.background.paper, 0.95)
                    : alpha("#ffffff", 0.98),
                  backdropFilter: "saturate(180%) blur(20px)",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                } 
              },
            }}
          >
            <Box
              sx={{
                px: 2,
                pt: 1,
                pb: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle1" fontWeight={800}>
                Notifications
              </Typography>
              <Button
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAllAsRead}
                disabled={!unseenNotifications.length || notifLoading}
              >
                Mark all
              </Button>
            </Box>
            <Divider />

            {notifLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={22} />
              </Box>
            ) : unseenNotifications.length === 0 ? (
              <Box
                sx={{
                  px: 2,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ChatBubbleOutlineIcon fontSize="small" color="disabled" />
                <Typography color="text.secondary">
                  No new notifications
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 360, overflowY: "auto", px: 1.25 }}>
                {unseenNotifications.map((notif) => (
                  <Paper
                    key={notif.id}
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: alpha(theme.palette.background.paper, 0.6),
                      backdropFilter: "blur(10px)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        transform: "translateX(4px) translateY(-2px)",
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    }}
                    onClick={() => {
                      handleSeenNotification(notif.id);
                      handleNotificationItemClick(notif.entityId);
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        gap: 1,
                        alignItems: "start",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                          color: theme.palette.primary.main,
                        }}
                      >
                        <NotificationsActiveIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={700} variant="body2" noWrap>
                          {notif.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {notif.body}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {timeAgo(notif.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
            <Divider sx={{ mt: 0.5 }} />
            <Box display="flex" justifyContent="center" p={1}>
              <Button
                variant="text"
                onClick={() => {
                  withProgressNav("/notifications");
                  handleNotifClose();
                }}
              >
                Show all
              </Button>
            </Box>
          </Menu>

          {/* Enhanced Profile */}
          <StyledIconButton 
            edge="end" 
            color="inherit" 
            onClick={handleProfileMenuOpen} 
            size="large"
            sx={{
              borderRadius: 2.5,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              p: 0.5,
            }}
          >
            {profileImage ? (
              <Avatar 
                src={profileImage} 
                alt="User profile" 
                sx={{ 
                  width: 36, 
                  height: 36,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }} 
              />
            ) : usernameInitial ? (
              <Avatar 
                sx={{ 
                  width: 36, 
                  height: 36,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  color: theme.palette.primary.contrastText,
                  fontWeight: 700,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {usernameInitial}
              </Avatar>
            ) : (
              <Avatar 
                sx={{ 
                  width: 36, 
                  height: 36,
                  bgcolor: alpha(theme.palette.grey[500], 0.2),
                  border: `2px solid ${alpha(theme.palette.grey[500], 0.3)}`,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                ?
              </Avatar>
            )}
          </StyledIconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{ sx: { width: 260, borderRadius: 2 } }}
          >
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar src={profileImage || undefined} sx={{ width: 36, height: 36 }}>
                  {(!profileImage && usernameInitial) || "?"}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {user?.fullName || user?.username || "User"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {workspaceName}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Divider />
            <MenuItem
              component={Link}
              to="/settings"
              onClick={handleProfileMenuClose}
              sx={{ color: theme.palette.text.secondary }}
            >
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogoutClick} sx={{ color: theme.palette.text.secondary }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Box>

        {/* Logout dialog */}
        <Dialog open={openLogout} onClose={handleLogoutCancel}>
          <DialogTitle>Confirm Logout</DialogTitle>
          <DialogContent>
            <DialogContentText>Are you sure you want to log out?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleLogoutCancel}>No</Button>
            <Button onClick={handleLogoutConfirm} color="primary" variant="contained">
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Toolbar>

      {/* Enhanced Command Palette */}
      <Dialog
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.95)
                : alpha("#ffffff", 0.98),
            backdropFilter: "saturate(180%) blur(20px)",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.15)}`,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.25 }}>
          <SearchIcon sx={{ opacity: 0.7 }} />
          <TextField
            autoFocus
            variant="standard"
            fullWidth
            placeholder="Type a command or search…"
            value={cmdQuery}
            onChange={(e) => {
              setCmdQuery(e.target.value);
              setCmdIndex(0);
            }}
            InputProps={{ disableUnderline: true }}
          />
          <Box
            sx={{
              border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
              borderRadius: 1,
              px: 0.75,
              py: 0.25,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "text.secondary",
            }}
            title="Command shortcut"
          >
            <KeyboardCommandKeyIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">K</Typography>
          </Box>
        </Box>
        <Divider />

        {/* Results */}
        <Box sx={{ maxHeight: 360, overflowY: "auto", py: 0.5 }}>
          {filtered.length === 0 ? (
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "text.secondary",
              }}
            >
              <ChatBubbleOutlineIcon fontSize="small" />
              <Typography>No results</Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {filtered.map((c, i) => {
                const active = i === cmdIndex;
                return (
                  <ListItemButton
                    key={c.label}
                    onClick={() => {
                      setCmdOpen(false);
                      if (c.action) c.action();
                      else if (c.path) withProgressNav(c.path);
                    }}
                    selected={active}
                    sx={{
                      py: 1.25,
                      px: 2,
                      borderRadius: 1.5,
                      mx: 0.5,
                      mb: 0.5,
                      transition: "all 0.2s ease",
                      "&.Mui-selected": {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        transform: "translateX(4px)",
                      },
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                      {c.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={c.label}
                      secondary={c.hint}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Dialog>

      {/* Chat Dialog */}
      <ChatDialog 
        open={chatDialogOpen} 
        onClose={() => setChatDialogOpen(false)} 
      />

      {/* Approvals Dialog */}
      <ApprovalsDialog 
        open={approvalsDialogOpen} 
        onClose={() => setApprovalsDialogOpen(false)} 
      />
    </AppBar>
  );
};

export default MyAppBar;
export const APP_BAR_HEIGHT = 72; // <— export this